import { Agent, Channel } from '@nemuru-eng/js-api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SimulatorComponent } from '@/components/modules/Simulator/components/SimulatorComponent';
import { SimulatorDisabledAlert } from '@/components/modules/Simulator/components/SimulatorDisabledAlert';
import { AgentSelectionComponent } from '@/components/shared/components/AgentSelectionComponent/AgentSelectionComponent';
import { isDevelopment } from '@/components/shared/components/DevelopOptionsPanel/DevelopOptionsPanel';
import { useSimulator } from '@/components/shared/hooks/useSimulator';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useUser } from '@/hooks/useUser';
import { MerchantInStructure } from '@/types/MerchantInStructure';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const SimulatorModule = () => {
  const { userEntity, isUserAgent } = useUser();
  const { simulatorComponent } = useSimulator();
  const { themeName } = useCustomTheme();

  const [state, setState] = useState<{
    selectedMerchant: MerchantInStructure | undefined;
    selectedChannel: Channel | undefined;
    selectedAgent: Agent | undefined;
  }>({
    selectedMerchant: userEntity?.merchant,
    selectedChannel: userEntity?.channel,
    selectedAgent: userEntity?.agent,
  });

  const { selectedMerchant, selectedChannel, selectedAgent } = state;

  const disabled = useMemo(() => !isEnabledByAgent(selectedAgent), [selectedAgent]);

  useEffect(() => {
    if (!userEntity) return;
    setState(() => ({
      selectedMerchant: userEntity.merchant,
      selectedChannel: userEntity.channel,
      selectedAgent: userEntity.agent,
    }));
  }, [userEntity]);

  // Mount simulator (themeName must be added to useEffect dependencies because that allows to mount again after the last rerender of the component)
  useEffect(() => {
    if (!disabled) {
      const mountSimulator = async () => {
        const Simulator = await simulatorComponent(
          {
            options: {
              showDevPanel: isDevelopment,
            },
            onSelected: (conditions) => console.log('-> onSelected', conditions),
            onError: (error) => console.log('-> onError', error),
          },
          selectedAgent,
        );
        if (!Simulator) return;
        Simulator.mount('#nemuru-simulator');
      };

      mountSimulator();
    }
  }, [disabled, themeName, selectedAgent]);

  const resetItem = useCallback(() => true, [userEntity]);

  return (
    <div>
      {!isUserAgent && (
        <div className="mb-4 max-w-auto">
          <AgentSelectionComponent
            initialValues={{ selectedAgent, selectedChannel, selectedMerchant }}
            onChange={(nextAgent, nextChannel, nextMerchant) => {
              setState(() => ({
                selectedMerchant: nextMerchant,
                selectedChannel: nextChannel,
                selectedAgent: nextAgent,
              }));
            }}
            error={disabled ? 'SIMULATOR_DISABLED' : undefined}
            resetItem={resetItem}
          />
        </div>
      )}
      {!disabled && !!selectedAgent && <SimulatorComponent expandable />}
      {disabled && isUserAgent && <SimulatorDisabledAlert />}
    </div>
  );
};

export const isEnabledByAgent = (agent: Agent | undefined) => {
  if (!agent) return false;
  if (agent.allowedPaymentMethodTypes.length === 0) return false;
  return true;
};

export const Simulator = withPermissionsModule(SimulatorModule);
